import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';

// Pages
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import TurnaroundStats from './pages/TurnaroundStats';
import Clients from './pages/Clients';
import ClientDetail from './pages/ClientDetail';
import Jobs from './pages/Jobs';
import JobDetail from './pages/JobDetail';
import JobViewV2 from './pages/JobViewV2';
import Calendar from './pages/Calendar';
import Schedule from './pages/Schedule';
import QCDashboard from './pages/QCDashboard';
import QCReview from './pages/QCReview';
import ClientPortalPage from './pages/ClientPortal';
import ClientHub from './pages/ClientHub';
import DigitalCard from './pages/DigitalCard';
import EmailInbox from './pages/EmailInbox';
import AgencyDirectory from './pages/AgencyDirectory';
import DailySummary from './pages/DailySummary';
import Map from './pages/Map';
import CommandCenter from './pages/CommandCenter';
import SearchResults from './pages/SearchResults';
import Team from './pages/Team';
import TimeTracking from './pages/TimeTracking';
import Timesheet from './pages/Timesheet';
import Payroll from './pages/Payroll';
import Holidays from './pages/Holidays';
import TimeOff from './pages/TimeOff';
import Todos from './pages/Todos';
import Settings from './pages/Settings';
import Profile from './pages/Profile';
import Billing from './pages/Billing';
import InvoiceForm from './pages/InvoiceForm';
import InvoiceView from './pages/InvoiceView';
import QuoteForm from './pages/QuoteForm';
import QuoteView from './pages/QuoteView';
import CompanySettings from './pages/CompanySettings';
import Supplies from './pages/Supplies';
import Safety from './pages/Safety';
import Training from './pages/Training';
import TrainingModule from './pages/TrainingModule';
import TrainingLesson from './pages/TrainingLesson';
import TrainingExam from './pages/TrainingExam';
import TrainingAdmin from './pages/TrainingAdmin';
import CareerRoster from './pages/CareerRoster';
import MyCareerPath from './pages/MyCareerPath';
import PublicQuoteApproval from './pages/PublicQuoteApproval';
import PublicChangeOrderApproval from './pages/PublicChangeOrderApproval';
import ClientIntake from './pages/ClientIntake';
import PublicQuoteRequestForm from './pages/PublicQuoteRequestForm';
import Nominations from './pages/Nominations';
import NominationsAdmin from './pages/NominationsAdmin';
import Certificates from './pages/Certificates';
import Letters from './pages/Letters';
import LetterEdit from './pages/LetterEdit';

// Components
import Navigation from './components/Navigation';
import MobileLayout from './components/MobileLayout';
import { ThemeProvider } from './components/ThemeProvider';
import AIAssistant from './components/AIAssistant';
import SupplyCheckModal from './components/SupplyCheckModal';
import ToolboxTalkReminder from './components/ToolboxTalkReminder';
import OfflineBanner from './components/OfflineBanner';
import { getMySupplyChecks, getMyEquipmentChecks, checkToolboxTalkDue } from './services/api';

// Styles
import './styles/App.css';

import { isMobileLayout } from './utils/deviceLayout';

// Detect mobile device.
//
// The mobile and desktop branches below are different component trees, so every
// flip of this value tears the whole page down and rebuilds it from scratch —
// losing anything held in page state. isMobileLayout() is orientation-stable for
// that reason: rotating a phone must not count as becoming a desktop.
const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(isMobileLayout);

  useEffect(() => {
    const checkMobile = () => setIsMobile(isMobileLayout());

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  return isMobile;
};

function AppContent() {
  const isMobile = useIsMobile();
  const { user } = useAuth();
  const location = useLocation();
  const [showAssistant, setShowAssistant] = useState(false);
  const [showSupplyCheck, setShowSupplyCheck] = useState(false);
  const [hasPendingChecks, setHasPendingChecks] = useState(false);
  const [supplyCheckCategories, setSupplyCheckCategories] = useState([]);
  const [supplyCheckEquipment, setSupplyCheckEquipment] = useState([]);
  const [showToolboxTalkPrompt, setShowToolboxTalkPrompt] = useState(false);
  const [toolboxTalkDue, setToolboxTalkDue] = useState(false);
  const [suggestedTopic, setSuggestedTopic] = useState(null);
  const [toolboxTalkSeason, setToolboxTalkSeason] = useState('');

  // Only show AI assistant to admins (users who can manage payroll)
  const canUseAssistant = user?.can_manage_payroll;

  // Daily supply/equipment check — fetch on login
  useEffect(() => {
    if (!user) return;
    const todayKey = `checkDate_${user.id}`;
    const today = new Date().toISOString().split('T')[0];
    if (localStorage.getItem(todayKey) === today) return;

    const checkDue = async () => {
      try {
        const [supplyRes, equipRes] = await Promise.all([
          getMySupplyChecks().catch(() => ({ data: { categories: [] } })),
          getMyEquipmentChecks().catch(() => ({ data: { equipment: [] } }))
        ]);
        const cats = supplyRes.data.categories || [];
        const equips = equipRes.data.equipment || [];
        if (cats.length > 0 || equips.length > 0) {
          setSupplyCheckCategories(cats);
          setSupplyCheckEquipment(equips);
          setHasPendingChecks(true);
          setShowSupplyCheck(true);
        }
      } catch (err) {
        console.error('Error checking supply/equipment status:', err);
      }
    };
    checkDue();
  }, [user]);

  // Weekly toolbox talk check
  useEffect(() => {
    if (!user) return;
    const dismissKey = `toolboxTalkDismissed_${user.id}_${new Date().toISOString().split('T')[0]}`;
    if (localStorage.getItem(dismissKey)) return;

    const checkDue = async () => {
      try {
        const res = await checkToolboxTalkDue();
        if (res.data.talk_due) {
          setToolboxTalkDue(true);
          setSuggestedTopic(res.data.suggested_topic);
          setToolboxTalkSeason(res.data.current_season || '');
          // Auto-prompt on Mondays
          if (new Date().getDay() === 1) {
            setShowToolboxTalkPrompt(true);
          }
        } else {
          setToolboxTalkDue(false);
        }
      } catch (err) {
        console.error('Error checking toolbox talk status:', err);
      }
    };
    checkDue();
  }, [user]);

  // Re-show modal on every page navigation if checks are still pending
  useEffect(() => {
    if (hasPendingChecks) {
      setShowSupplyCheck(true);
    }
  }, [location.pathname, hasPendingChecks]);

  const routes = (
    <Routes>
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route path="/dashboard" element={<Dashboard />} />
      <Route path="/email-inbox" element={<EmailInbox />} />
      <Route path="/directory" element={<AgencyDirectory />} />
      <Route path="/clients" element={<Clients />} />
      <Route path="/clients/:id" element={<ClientDetail />} />
      <Route path="/jobs" element={<Jobs />} />
      <Route path="/jobs/:id" element={<JobViewV2 />} />
      <Route path="/jobs/:id/v2" element={<JobViewV2 />} />
      {/* Classic job view kept as a fallback (used by "Edit Details"); no longer the default. */}
      <Route path="/jobs/:id/classic" element={<JobDetail />} />
      <Route path="/calendar" element={<Calendar />} />
      <Route path="/schedule" element={<Schedule />} />
      <Route path="/analytics/turnaround" element={<TurnaroundStats />} />
      <Route path="/qc" element={<QCDashboard />} />
      <Route path="/qc/:id" element={<QCReview />} />
      <Route path="/map" element={<Map />} />
      <Route path="/search" element={<SearchResults />} />
      <Route path="/team" element={<Team />} />
      <Route path="/time-clock" element={<TimeTracking />} />
      <Route path="/daily-summary" element={<DailySummary />} />
      <Route path="/timesheets" element={<Timesheet />} />
      <Route path="/payroll" element={<Payroll />} />
      <Route path="/holidays" element={<Holidays />} />
      <Route path="/time-off" element={<TimeOff />} />
      <Route path="/todos" element={<Todos />} />
      <Route path="/billing" element={<Billing />} />
      <Route path="/billing/invoices/new" element={<InvoiceForm />} />
      <Route path="/billing/invoices/:id" element={<InvoiceView />} />
      <Route path="/billing/invoices/:id/edit" element={<InvoiceForm />} />
      <Route path="/billing/quotes/new" element={<QuoteForm />} />
      <Route path="/billing/quotes/:id" element={<QuoteView />} />
      <Route path="/billing/quotes/:id/edit" element={<QuoteForm />} />
      <Route path="/settings" element={<Settings />} />
      <Route path="/settings/company" element={<CompanySettings />} />
      <Route path="/profile" element={<Profile />} />
      <Route path="/supplies" element={<Supplies />} />
      <Route path="/safety" element={<Safety />} />
      <Route path="/training" element={<Training />} />
      <Route path="/training/admin" element={<TrainingAdmin />} />
      <Route path="/team/career-roster" element={<CareerRoster />} />
      <Route path="/my-career-path" element={<MyCareerPath />} />
      <Route path="/training/modules/:id" element={<TrainingModule />} />
      <Route path="/training/lessons/:id" element={<TrainingLesson />} />
      <Route path="/training/exam/:attemptId" element={<TrainingExam />} />
      <Route path="/nominations" element={<Nominations />} />
      <Route path="/nominations/admin" element={<NominationsAdmin />} />
      <Route path="/nominations/certificates" element={<Certificates />} />
      <Route path="/letters" element={<Letters />} />
      <Route path="/letters/new" element={<LetterEdit />} />
      <Route path="/letters/:id" element={<LetterEdit />} />
    </Routes>
  );

  const handleSupplyCheckDismiss = () => {
    setShowSupplyCheck(false);
  };

  const handleSupplyCheckComplete = () => {
    setShowSupplyCheck(false);
    setHasPendingChecks(false);
    const today = new Date().toISOString().split('T')[0];
    localStorage.setItem(`checkDate_${user.id}`, today);
  };

  const handleToolboxTalkDismiss = () => {
    setShowToolboxTalkPrompt(false);
    const today = new Date().toISOString().split('T')[0];
    localStorage.setItem(`toolboxTalkDismissed_${user.id}_${today}`, 'true');
  };

  const ToolboxTalkBanner = () => (
    <div
      onClick={() => setShowToolboxTalkPrompt(true)}
      style={{
        backgroundColor: '#dbeafe',
        borderBottom: '2px solid #3b82f6',
        padding: '10px 20px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '10px',
        cursor: 'pointer',
        zIndex: 998
      }}
    >
      <span style={{ fontSize: '16px' }}>&#128737;</span>
      <span style={{ fontSize: '14px', fontWeight: 600, color: '#1e40af' }}>
        Weekly safety talk not yet completed.
      </span>
      <span style={{ fontSize: '12px', color: '#1d4ed8', textDecoration: 'underline' }}>
        Start now
      </span>
    </div>
  );

  // Persistent warning banner
  const PendingChecksBanner = () => (
    <div
      onClick={() => setShowSupplyCheck(true)}
      style={{
        backgroundColor: '#fef3c7',
        borderBottom: '2px solid #f59e0b',
        padding: '10px 20px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '10px',
        cursor: 'pointer',
        zIndex: 998
      }}
    >
      <span style={{ fontSize: '16px' }}>&#9888;</span>
      <span style={{ fontSize: '14px', fontWeight: 600, color: '#92400e' }}>
        You have pending supply/equipment checks due today.
      </span>
      <span style={{
        fontSize: '12px',
        color: '#b45309',
        textDecoration: 'underline'
      }}>
        Complete now
      </span>
    </div>
  );

  if (isMobile) {
    return (
      <MobileLayout>
        <OfflineBanner />
        {hasPendingChecks && !showSupplyCheck && <PendingChecksBanner />}
        {toolboxTalkDue && !showToolboxTalkPrompt && <ToolboxTalkBanner />}
        {routes}
        {showToolboxTalkPrompt && (
          <ToolboxTalkReminder
            suggestedTopic={suggestedTopic}
            currentSeason={toolboxTalkSeason}
            onDismiss={handleToolboxTalkDismiss}
            onNavigate={() => setShowToolboxTalkPrompt(false)}
          />
        )}
        {showSupplyCheck && (
          <SupplyCheckModal
            categories={supplyCheckCategories}
            equipment={supplyCheckEquipment}
            onClose={handleSupplyCheckDismiss}
            onComplete={handleSupplyCheckComplete}
          />
        )}
        {canUseAssistant && (
          <>
            <button
              onClick={() => setShowAssistant(!showAssistant)}
              style={{
                position: 'fixed',
                bottom: '80px',
                right: '16px',
                width: '50px',
                height: '50px',
                borderRadius: '50%',
                backgroundColor: showAssistant ? '#dc3545' : '#1a73e8',
                color: 'white',
                border: 'none',
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '22px',
                zIndex: 999
              }}
            >
              {showAssistant ? '×' : '🤖'}
            </button>
            <AIAssistant isOpen={showAssistant} onClose={() => setShowAssistant(false)} />
          </>
        )}
      </MobileLayout>
    );
  }

  // Floating assistant button component
  const AssistantButton = () => (
    <>
      <button
        onClick={() => setShowAssistant(!showAssistant)}
        style={{
          position: 'fixed',
          bottom: '20px',
          right: '20px',
          width: '56px',
          height: '56px',
          borderRadius: '50%',
          backgroundColor: showAssistant ? '#dc3545' : '#1a73e8',
          color: 'white',
          border: 'none',
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '24px',
          zIndex: 999,
          transition: 'background-color 0.2s, transform 0.2s'
        }}
        title={showAssistant ? 'Close Assistant' : 'Open AI Assistant'}
      >
        {showAssistant ? '×' : '🤖'}
      </button>
      <AIAssistant isOpen={showAssistant} onClose={() => setShowAssistant(false)} />
    </>
  );

  return (
    <div className="app">
      <Navigation />
      <div className="main-content">
        <OfflineBanner />
        {hasPendingChecks && !showSupplyCheck && <PendingChecksBanner />}
        {toolboxTalkDue && !showToolboxTalkPrompt && <ToolboxTalkBanner />}
        {routes}
      </div>
      {canUseAssistant && <AssistantButton />}
      {showToolboxTalkPrompt && (
        <ToolboxTalkReminder
          suggestedTopic={suggestedTopic}
          currentSeason={toolboxTalkSeason}
          onDismiss={handleToolboxTalkDismiss}
          onNavigate={() => setShowToolboxTalkPrompt(false)}
        />
      )}
      {showSupplyCheck && (
        <SupplyCheckModal
          categories={supplyCheckCategories}
          equipment={supplyCheckEquipment}
          onClose={handleSupplyCheckDismiss}
          onComplete={handleSupplyCheckComplete}
        />
      )}
    </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <Router>
        <Routes>
          {/* Public Route */}
          <Route path="/login" element={<Login />} />
          
          {/* Public Portal (no auth required) */}
          <Route path="/portal/:token" element={<ClientPortalPage />} />
          <Route path="/client/:token" element={<ClientHub />} />

          {/* Public digital business card (no auth required) */}
          <Route path="/card/:slug" element={<DigitalCard />} />

          {/* Public Quote Approval (no auth required) */}
          <Route path="/quote/:token" element={<PublicQuoteApproval />} />

          {/* Public Change Order Approval (no auth required) */}
          <Route path="/change-order/:token" element={<PublicChangeOrderApproval />} />

          {/* Public Client Intake form (no auth required) */}
          <Route path="/new-client" element={<ClientIntake />} />

          {/* Public Quote Request form (no auth required) */}
          <Route path="/get-a-quote" element={<PublicQuoteRequestForm />} />

          {/* Command Center — authenticated, but deliberately OUTSIDE AppContent.
              It runs full-screen on the office wall display and must not carry the
              nav sidebar, mobile chrome, or the supply-check / toolbox-talk modals
              that pop up for a logged-in person. */}
          <Route path="/command-center" element={
            <ProtectedRoute>
              <CommandCenter />
            </ProtectedRoute>
          } />

          {/* Protected Routes */}
          <Route path="/*" element={
            <ProtectedRoute>
              <ThemeProvider>
                <AppContent />
              </ThemeProvider>
            </ProtectedRoute>
          } />
        </Routes>
      </Router>
    </AuthProvider>
  );
}

export default App;